import { useEffect, useState, type RefObject } from 'react';

/**
 * Tracks whether the container has dropped below `threshold`.
 *
 * A container query would express this in CSS alone, but stacking has to
 * reorder the DOM — the narrow layout puts a different segment first, and CSS
 * `order` would leave the tab sequence following the wide layout.
 */
export function useIsStacked(
  ref: RefObject<HTMLElement | null>,
  threshold: number,
) {
  const [isStacked, setIsStacked] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setIsStacked(entry.contentRect.width < threshold);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, threshold]);

  return isStacked;
}

/**
 * Tracks the container's own width, so a `select` segment's open menu can be
 * floored to it — a "hug" segment's trigger gives width back to the other half
 * once the field is tight, and the menu has the whole field to open over.
 */
export function useContainerWidth(ref: RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return width;
}
